import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useDebounce } from "use-debounce";

import { sourcesApiSlice } from "api/sourcesApi.slice";
import { FilterButton, InfinityLoader } from "common/components";
import { UIInput } from "common/components/form/UIInput";
import { useTranslate } from "common/utilities/useTranslate";
import { activateModal } from "domains/modal/actions";
import { EditableSourceDetailSection } from "domains/moderate/sources/components/EditableSourceDetailSection/EditableSourceDetailSection";
import { SourceListItem } from "domains/moderate/sources/components/SourceListItem/SourceListItem";
import * as Styled from "domains/moderate/sources/components/SourcesPage/SourcesPage.styled";
import { getNoOfActiveSourceFilters, getSourceFilters } from "domains/moderate/sources/selectors";
import { getCanEditSources } from "entities/permissions/selectors";

export const SourcesPage = () => {
  const dispatch = useDispatch();
  const { sourceId } = useParams();
  const [selectedSourceId, setSelectedSourceId] = useState<string | undefined>(sourceId);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const translate = useTranslate();

  const activeSourceFilters = useSelector(getSourceFilters);
  const noOfActiveFilters = useSelector(getNoOfActiveSourceFilters);
  const canEditSources = useSelector(getCanEditSources);
  const {
    data: paginatedSources,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = sourcesApiSlice.endpoints.getSources.useInfiniteQuery({
    searchQuery: debouncedSearchQuery,
    filters: activeSourceFilters,
  });

  const sources = useMemo(() => paginatedSources?.pages.flat() ?? [], [paginatedSources?.pages]);

  const loadNextPage = async () => {
    if (isFetching || isFetchingNextPage) return;

    await fetchNextPage();
  };

  const openFilterModal = () => dispatch(activateModal({ id: "FilterSourceList" }));
  const openAddSourceModal = () => dispatch(activateModal({ id: "AddSourceForm" }));

  useEffect(() => {
    if (!sourceId) {
      if (sources.length === 0 || selectedSourceId === sources[0].id) return;

      setSelectedSourceId(sources[0].id);
    }

    if (sourceId === selectedSourceId) return;

    setSelectedSourceId(sourceId);
  }, [selectedSourceId, sourceId, sources]);

  return (
    <Styled.SourcePageContainer>
      <Styled.SourcePageLayoutContainer>
        <Styled.SourceListLayoutContainer>
          <Styled.Heading>{translate("app.moderate.sources.title")}</Styled.Heading>
          <Styled.SourceSearchAndFilterContainer>
            <UIInput
              name={translate("app.moderate.sources.search.input")}
              onChange={(value) => setSearchQuery(value)}
              placeholder={translate("app.moderate.sources.search.placeholder")}
              value={searchQuery}
              clear={() => setSearchQuery("")}
            />
            <Styled.ButtonsContainer>
              <FilterButton count={noOfActiveFilters} onClick={openFilterModal} />
              <Styled.AddButton title="Add Source" onClick={openAddSourceModal} disabled={!canEditSources}>
                <Styled.AddIcon width={14} height={14} />
              </Styled.AddButton>
            </Styled.ButtonsContainer>
          </Styled.SourceSearchAndFilterContainer>
          <Styled.ScrollableSourceListContainer>
            <InfinityLoader hasNextPage={hasNextPage} isPending={isFetching} loadNextPage={loadNextPage}>
              <Styled.SourcesListContainer>
                {sources.map((source) => (
                  <SourceListItem
                    key={source.id}
                    isSelected={selectedSourceId === source.id}
                    id={source.id}
                    name={source.name}
                    imageUrl={source.favicon}
                    type={source.type}
                    publicationType={source.publication_type}
                    isActive={source.active}
                  />
                ))}
              </Styled.SourcesListContainer>
            </InfinityLoader>
          </Styled.ScrollableSourceListContainer>
        </Styled.SourceListLayoutContainer>
        <EditableSourceDetailSection sourceId={selectedSourceId} />
      </Styled.SourcePageLayoutContainer>
    </Styled.SourcePageContainer>
  );
};
